/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  useDisclosure,
  useToast,
  Box,
  Text,
} from '@chakra-ui/react';
import { useQueryClient } from 'react-query';
import Select, { MultiValue, StylesConfig } from 'react-select';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import {
  DashboardHooks,
  useAgreementPlans,
} from '@partiu/api/dashboard/hooks.ts';
import { IPlan, ICreateMapIntegrationPlanPayload } from '@partiu/api/dashboard/types.ts';
import { createMapIntegrationPlanStaff } from '@partiu/api/dashboard/api.ts';

interface PlanOption {
  value: number;
  label: string;
  plan: IPlan;
}

interface OpenData {
  plan_slug_integration: string;
}

export interface CreateMapIntegrationPlanModalRef {
  open: (data: OpenData) => void;
  close: () => void;
}

interface Props {
  agreementId?: number;
  onCreated?: () => void;
}

export const CreateMapIntegrationPlanModal = forwardRef<CreateMapIntegrationPlanModalRef, Props>(
  ({ agreementId, onCreated }, ref) => {
    const { colors } = useTheme();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const queryClient = useQueryClient();
    const [selectedPlans, setSelectedPlans] = useState<PlanOption[]>([]);
    const [planSlugIntegration, setPlanSlugIntegration] = useState<string>('');
    const [isCreating, setIsCreating] = useState(false);

    // Planos reais do agreement logado
    const { data: agreementPlansData } = useAgreementPlans();

    const planOptions = useMemo(() => {
      const plans: IPlan[] = (agreementPlansData as any)?.results ?? agreementPlansData ?? [];
      if (!Array.isArray(plans)) return [];
      return plans.map((p: IPlan) => ({
        value: p.id,
        label: p.displayName || p.display_name || p.name || `Plano #${p.id}`,
        plan: p,
      }));
    }, [agreementPlansData]);

    const handleClose = () => {
      onClose();
      setSelectedPlans([]);
      setPlanSlugIntegration('');
      setIsCreating(false);
    };

    const handlePlanSelectionChange = (selectedOptions: MultiValue<PlanOption>) => {
      setSelectedPlans(selectedOptions as PlanOption[]);
    };

    const handleSubmit = async () => {
      if (selectedPlans.length === 0) {
        toast({ title: 'Erro', description: 'Selecione pelo menos um plano', status: 'error', duration: 3000 });
        return;
      }

      if (!planSlugIntegration.trim()) {
        toast({ title: 'Erro', description: 'Slug de integração é obrigatório', status: 'error', duration: 3000 });
        return;
      }

      setIsCreating(true);
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      try {
        // Criar mapeamentos para cada plano selecionado
        const promises = selectedPlans.map(async (planOption) => {
          const payload: ICreateMapIntegrationPlanPayload = {
            plan: planOption.value,
            plan_slug_integration: planSlugIntegration,
          };
          if (agreementId) payload.agreement = agreementId;

          try {
            await createMapIntegrationPlanStaff(payload);
            successCount++;
          } catch (error: any) {
            errorCount++;
            const msg = error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Erro desconhecido';
            errors.push(`${planOption.label}: ${typeof msg === 'string' ? msg : JSON.stringify(msg)}`);
          }
        });

        await Promise.allSettled(promises);

        // Invalidar queries após todas as criações (atualiza comparação ERP ↔ mapeamentos)
        queryClient.refetchQueries([DashboardHooks.AllMapIntegrationPlans]);
        queryClient.refetchQueries([DashboardHooks.PlansIntegration]);
        queryClient.refetchQueries([DashboardHooks.ErpPlans]);

        if (successCount > 0) {
          queryClient.refetchQueries([DashboardHooks.Modules]);
          onCreated?.();
          toast({
            title: successCount === selectedPlans.length ? 'Mapeamentos criados com sucesso!' : 'Mapeamentos parcialmente criados',
            description: `${successCount} de ${selectedPlans.length} mapeamento(s) criado(s)${errorCount > 0 ? `. ${errorCount} erro(s) ocorreram.` : ''}`,
            status: successCount === selectedPlans.length ? 'success' : 'warning',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Erro ao criar mapeamentos',
            description: errors.length > 0 ? errors.slice(0, 3).join('; ') : 'Não foi possível criar os mapeamentos',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }

        if (successCount > 0) {
          handleClose();
        } else {
          setIsCreating(false);
        }
      } catch (error) {
        setIsCreating(false);
        toast({
          title: 'Erro',
          description: 'Ocorreu um erro inesperado ao criar os mapeamentos',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    const selectStyles: StylesConfig<PlanOption, true> = {
      control: (provided) => ({
        ...provided,
        fontSize: '13px',
        minHeight: '38px',
        borderColor: selectedPlans.length === 0 ? '#E53E3E' : '#E2E8F0',
      }),
      menu: (provided) => ({
        ...provided,
        zIndex: 10,
        fontSize: '13px',
      }),
      multiValue: (provided) => ({
        ...provided,
        backgroundColor: '#ebf8ff',
        borderRadius: '6px',
      }),
      multiValueLabel: (provided) => ({
        ...provided,
        color: '#2b6cb0',
        fontSize: '13px',
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        color: '#2b6cb0',
        ':hover': {
          backgroundColor: '#bee3f8',
          color: '#1e4a72',
        },
      }),
      placeholder: (provided) => ({
        ...provided,
        color: '#a0aec0',
        fontSize: '14px',
      }),
      option: (provided, state) => ({
        ...provided,
        fontSize: '14px',
        backgroundColor: state.isSelected ? '#3182ce' : state.isFocused ? '#ebf8ff' : 'white',
        color: state.isSelected ? 'white' : '#2d3748',
        ':active': {
          backgroundColor: '#2c5aa0',
        },
      }),
    };

    useImperativeHandle(ref, () => ({
      open: (data: OpenData) => {
        setPlanSlugIntegration(data.plan_slug_integration);
        setSelectedPlans([]);
        setIsCreating(false);
        onOpen();
      },
      close: handleClose,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [onOpen]);

    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="md">
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent>
          <ModalHeader fontSize="md" color={colors.primaryColor} pb={2}>
            Criar Mapeamento
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={3}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={selectedPlans.length === 0}>
                <FormLabel fontSize="xs" fontWeight="medium" mb={1}>Planos *</FormLabel>
                <Box width="100%">
                  <Text fontSize="xs" color="gray.600" fontWeight="medium" mb={2}>
                    Selecione os planos ({selectedPlans.length} selecionado(s))
                  </Text>
                  <Select<PlanOption, true>
                    isMulti
                    options={planOptions}
                    value={selectedPlans}
                    onChange={handlePlanSelectionChange}
                    placeholder="Buscar por nome do plano..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue?.trim() ? 'Nenhum plano encontrado para esta busca' : 'Nenhum plano encontrado'
                    }
                    isDisabled={isCreating}
                    styles={selectStyles}
                    closeMenuOnSelect={false}
                    isSearchable
                    menuPlacement="auto"
                    maxMenuHeight={200}
                    filterOption={(option, inputValue) => {
                      const search = (inputValue || '').trim().toLowerCase();
                      if (!search) return true;
                      const label = (option.label || '').toLowerCase();
                      return label.includes(search);
                    }}
                  />
                  {selectedPlans.length > 0 && (
                    <Text fontSize="xs" color="blue.600" mt={1}>
                      ✓ {selectedPlans.length} plano(s) selecionado(s)
                    </Text>
                  )}
                </Box>
                {selectedPlans.length === 0 && (
                  <FormErrorMessage fontSize="xs">Selecione pelo menos um plano</FormErrorMessage>
                )}
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" fontWeight="medium" mb={1}>Slug de Integração (ERP)</FormLabel>
                <Input
                  size="sm"
                  fontSize="xs"
                  value={planSlugIntegration}
                  readOnly
                  bg="gray.100"
                  cursor="not-allowed"
                  _hover={{ bg: 'gray.100' }}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Este campo não pode ser alterado
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter pt={2} pb={3}>
            <Button variant="ghost" size="sm" mr={2} onClick={handleClose} isDisabled={isCreating}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              onClick={handleSubmit}
              isLoading={isCreating}
              loadingText="Criando..."
              isDisabled={selectedPlans.length === 0}
            >
              Criar {selectedPlans.length > 1 ? `(${selectedPlans.length})` : ''}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
);

CreateMapIntegrationPlanModal.displayName = 'CreateMapIntegrationPlanModal';
